import React from 'react';
import { Dropdown, Tooltip } from 'antd';
import { MoreOutlined, EyeOutlined, CheckCircleOutlined, DollarOutlined } from '@ant-design/icons';
import { formatCurrency } from '../hooks/useSalary';

const statusConfig = {
    draft: { color: 'orange', label: 'Draft', bg: 'rgba(245, 158, 11, 0.1)', text: '#f59e0b', border: 'rgba(245, 158, 11, 0.2)' },
    pending: { color: 'purple', label: 'Waiting Approval', bg: 'rgba(147, 51, 234, 0.1)', text: '#a855f7', border: 'rgba(147, 51, 234, 0.2)' },
    disbursed: { color: 'green', label: 'Disbursed', bg: 'rgba(16, 185, 129, 0.1)', text: '#10b981', border: 'rgba(16, 185, 129, 0.2)' },
    finalized: { color: 'green', label: 'Disbursed', bg: 'rgba(16, 185, 129, 0.1)', text: '#10b981', border: 'rgba(16, 185, 129, 0.2)' },
};

export const getSalaryColumns = ({ handleViewRow, handleMarkStatus, canUpdate }) => [
    {
        title: 'Employee',
        key: 'employee',
        render: (_, r) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                    width: 38, height: 38, borderRadius: 12,
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0,
                    boxShadow: '0 2px 8px rgba(99, 102, 241, 0.25)',
                }}>
                    {(r.name ?? r.email ?? '?').charAt(0).toUpperCase()}
                </div>
                <div>
                    <div style={{ fontWeight: 600, color: 'var(--ant-color-text)', fontSize: 13 }}>{r.name}</div>
                    <div style={{ color: 'var(--ant-color-text-secondary)', fontSize: 11 }}>{r.email}</div>
                </div>
            </div>
        ),
    },
    {
        title: 'Base Salary',
        key: 'baseSalary',
        render: (_, r) => (
            <div>
                <div style={{ color: 'var(--ant-color-text)', fontWeight: 600, fontSize: 13 }}>
                    {formatCurrency(r.baseSalary)}
                </div>
            </div>
        ),
    },
    {
        title: 'Per Day Salary',
        key: 'dailySalary',
        render: (_, r) => {
            const base = parseFloat(r.baseSalary || 0);
            const perDay = base > 0 ? base / 22 : 0;
            return (
                <div style={{ color: 'var(--ant-color-text-secondary)', fontSize: 12, fontWeight: 500 }}>
                    {formatCurrency(perDay)}<span style={{ fontSize: 10, opacity: 0.7 }}>/day</span>
                </div>
            );
        },
    },
    {
        title: 'Attendance',
        key: 'attendance',
        render: (_, r) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Tooltip title="Present Days">
                    <span style={{
                        padding: '3px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                        background: 'rgba(16, 185, 129, 0.12)', color: '#10b981',
                        border: '1px solid rgba(16, 185, 129, 0.25)', display: 'inline-flex', alignItems: 'center', gap: 3
                    }}>
                        ✓ {r.presentDays}
                    </span>
                </Tooltip>
                <Tooltip title="Absent Days">
                    <span style={{
                        padding: '3px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                        background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444',
                        border: '1px solid rgba(239, 68, 68, 0.25)', display: 'inline-flex', alignItems: 'center', gap: 3
                    }}>
                        ✗ {r.absentDays}
                    </span>
                </Tooltip>
                <Tooltip title="Late Days">
                    <span style={{
                        padding: '3px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                        background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b',
                        border: '1px solid rgba(245, 158, 11, 0.25)', display: 'inline-flex', alignItems: 'center', gap: 3
                    }}>
                        ⏰ {r.lateDays}
                    </span>
                </Tooltip>
            </div>
        ),
    },
    {
        title: 'Deductions',
        key: 'deductions',
        render: (_, r) => (
            <span style={{ color: parseFloat(r.deductions || 0) > 0 ? '#ef4444' : 'var(--ant-color-text-secondary)', fontWeight: 600, fontSize: 12 }}>
                {parseFloat(r.deductions || 0) > 0 ? `-${formatCurrency(r.deductions)}` : '0.00'}
            </span>
        ),
    },
    {
        title: 'Net Salary',
        key: 'actualSalary',
        render: (_, r) => (
            <div style={{ color: '#10b981', fontWeight: 700, fontSize: 14 }}>
                {formatCurrency(r.actualSalary)}
            </div>
        ),
    },
    {
        title: 'Status',
        key: 'status',
        render: (_, r) => {
            const st = statusConfig[r.status ?? 'draft'] || statusConfig.draft;
            return (
                <span style={{
                    padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                    background: st.bg, color: st.text, border: `1px solid ${st.border}`,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                    {st.label}
                </span>
            );
        },
    },
    {
        title: 'Actions',
        key: 'actions',
        width: 80,
        render: (_, r) => {
            const actionItems = [
                {
                    key: 'view',
                    label: 'View Summary & Breakdown',
                    icon: <EyeOutlined />,
                    onClick: () => handleViewRow(r),
                },
            ];

            if (canUpdate) {
                if (r.status !== 'pending') {
                    actionItems.push({
                        key: 'mark_pending',
                        label: 'Mark as Waiting Approval',
                        icon: <CheckCircleOutlined style={{ color: '#a855f7' }} />,
                        onClick: () => handleMarkStatus(r, 'pending'),
                    });
                }
                if (r.status !== 'disbursed') {
                    actionItems.push({
                        key: 'mark_disbursed',
                        label: 'Disburse Salary',
                        icon: <DollarOutlined style={{ color: '#10b981' }} />,
                        onClick: () => handleMarkStatus(r, 'disbursed'),
                    });
                }
            }

            return (
                <Dropdown menu={{ items: actionItems }} trigger={['click']} placement="bottomRight">
                    <div style={{
                        width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', background: 'var(--ant-color-fill-alter, #f5f5f5)', color: 'var(--ant-color-text-secondary)',
                    }}>
                        <MoreOutlined style={{ fontSize: 16 }} />
                    </div>
                </Dropdown>
            );
        },
    },
];
